// Ilustrações dos estados vazios. Um ícone de 16px dentro de um quadradinho
// cinza é o mesmo desenho em toda tela vazia do app — não diz nada sobre o que
// está faltando ali. Cada ilustração daqui desenha o OBJETO que a tela ainda
// não tem (o papel, a automação, a fatura, a chave, o chamado), para o vazio
// parecer um começo e não um erro.
//
// Regras de composição, para as seis parecerem a mesma mão:
//  - viewBox 160×120 e nada fora dele;
//  - só `currentColor` (herdado de `text-muted-foreground`), com três pesos:
//    superfície (opacity ~.06), contorno (~.25) e conteúdo (~.2 a .35). Assim
//    funcionam em claro e escuro sem uma cor sequer codificada;
//  - um único acento em `text-primary` por ilustração — o elemento que
//    representa a ação que a pessoa vai fazer ali;
//  - `aria-hidden`: o texto do empty state é quem informa; a ilustração é
//    decoração.
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

function Illustration({
  className,
  children,
  groundY,
  groundRx,
  ...props
}: ComponentProps<"svg"> & { groundY: number; groundRx: number }) {
  return (
    <svg
      viewBox="0 0 160 120"
      fill="none"
      aria-hidden="true"
      className={cn("h-24 w-40 text-muted-foreground", className)}
      {...props}
    >
      {children}
      {/* Sombra de contato: sem ela o desenho fica boiando no card. Cada
          ilustração passa a própria linha do chão — uma elipse fixa ficava
          longe demais das composições mais baixas. */}
      <ellipse cx="80" cy={groundY} rx={groundRx} ry="5" className="fill-current opacity-[0.08]" />
    </svg>
  );
}

// Papéis e permissões: um papel é uma lista de telas marcadas — é isso que o
// cartão mostra, com a primeira permissão já ligada (o acento).
export function RolesIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={40} {...props}>
      <rect x="34" y="16" width="92" height="82" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="34"
        y="16"
        width="92"
        height="82"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="48" y="30" width="38" height="7" rx="3.5" className="fill-current opacity-30" />
      {[52, 68, 84].map((y, i) => (
        <g key={y}>
          <rect
            x="48"
            y={y}
            width="11"
            height="11"
            rx="3"
            className={i === 0 ? "stroke-current text-primary" : "stroke-current opacity-25"}
            strokeWidth="2"
          />
          <rect
            x="66"
            y={y + 3}
            width={i === 0 ? 46 : i === 1 ? 36 : 28}
            height="5"
            rx="2.5"
            className="fill-current opacity-20"
          />
        </g>
      ))}
      <path
        d="m50.5 57.5 2.5 2.5 4.5-4.5"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Automações: dois nós ligados por um gatilho. O nó de chegada é tracejado —
// é o pedaço que ainda não existe.
export function AutomationIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={88} groundRx={44} {...props}>
      <rect x="14" y="30" width="52" height="44" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="14"
        y="30"
        width="52"
        height="44"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="26" y="42" width="28" height="5" rx="2.5" className="fill-current opacity-30" />
      <rect x="26" y="54" width="18" height="5" rx="2.5" className="fill-current opacity-20" />
      <path
        d="M72 52h16"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="m84 46 6 6-6 6"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="98"
        y="30"
        width="52"
        height="44"
        rx="10"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeDasharray="6 5"
      />
      <path d="m127 38-10 15h8l-3 12 11-16h-8z" className="fill-current text-primary opacity-80" />
    </Illustration>
  );
}

// Financeiro: a fatura que ainda não fechou — recibo com serrilha embaixo e a
// linha do total em destaque.
export function InvoiceIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={102} groundRx={34} {...props}>
      <path
        d="M44 18a6 6 0 0 1 6-6h60a6 6 0 0 1 6 6v76l-9-6-9 6-9-6-9 6-9-6-9 6-9-6-9 6z"
        className="fill-current opacity-[0.06]"
      />
      <path
        d="M44 18a6 6 0 0 1 6-6h60a6 6 0 0 1 6 6v76l-9-6-9 6-9-6-9 6-9-6-9 6-9-6-9 6z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect x="56" y="28" width="34" height="6" rx="3" className="fill-current opacity-30" />
      {[46, 58].map((y) => (
        <g key={y}>
          <rect x="56" y={y} width="26" height="5" rx="2.5" className="fill-current opacity-20" />
          <rect x="88" y={y} width="16" height="5" rx="2.5" className="fill-current opacity-20" />
        </g>
      ))}
      <rect
        x="56"
        y="72"
        width="48"
        height="7"
        rx="3.5"
        className="fill-current text-primary opacity-70"
      />
    </Illustration>
  );
}

// Chaves de API: a chave sobre o cartão de credenciais. O anel é o acento —
// é o que a pessoa vai gerar.
export function ApiKeyIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={96} groundRx={40} {...props}>
      <rect x="34" y="26" width="92" height="62" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="34"
        y="26"
        width="92"
        height="62"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="46" y="38" width="30" height="5" rx="2.5" className="fill-current opacity-25" />
      <rect x="46" y="72" width="52" height="5" rx="2.5" className="fill-current opacity-20" />
      <circle cx="62" cy="58" r="10" className="stroke-current text-primary" strokeWidth="2.5" />
      <path
        d="M72 58h30m-8 0v8m-8-8v6"
        className="stroke-current text-primary"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Suporte: a conversa que ainda não começou. O balão da resposta é o acento —
// a promessa de que alguém responde.
export function TicketIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={104} groundRx={38} {...props}>
      <path
        d="M28 26a8 8 0 0 1 8-8h56a8 8 0 0 1 8 8v30a8 8 0 0 1-8 8H52l-16 12V64h-.5a8 8 0 0 1-7.5-8z"
        className="fill-current opacity-[0.06]"
      />
      <path
        d="M28 26a8 8 0 0 1 8-8h56a8 8 0 0 1 8 8v30a8 8 0 0 1-8 8H52l-16 12V64h-.5a8 8 0 0 1-7.5-8z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect x="44" y="32" width="40" height="5" rx="2.5" className="fill-current opacity-25" />
      <rect x="44" y="44" width="26" height="5" rx="2.5" className="fill-current opacity-20" />
      <path
        d="M112 52h20a6 6 0 0 1 6 6v20a6 6 0 0 1-6 6h-4v10l-12-10h-4a6 6 0 0 1-6-6V58a6 6 0 0 1 6-6z"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M120 64h12m-12 10h7"
        className="stroke-current text-primary opacity-60"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Monitoramento ao vivo: duas pontas de uma ligação unidas pela onda de áudio.
// O ponto aceso é a chamada que está acontecendo agora.
export function ActiveCallsIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={104} groundRx={44} {...props}>
      <rect x="18" y="31" width="43" height="54" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="18"
        y="31"
        width="43"
        height="54"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <circle cx="39.5" cy="49" r="8" className="fill-current opacity-20" />
      <rect x="29" y="64" width="21" height="5" rx="2.5" className="fill-current opacity-25" />
      <rect x="25" y="75" width="29" height="4" rx="2" className="fill-current opacity-15" />

      <rect x="99" y="31" width="43" height="54" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="99"
        y="31"
        width="43"
        height="54"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <circle cx="120.5" cy="49" r="8" className="fill-current opacity-20" />
      <rect x="110" y="64" width="21" height="5" rx="2.5" className="fill-current opacity-25" />
      <rect x="106" y="75" width="29" height="4" rx="2" className="fill-current opacity-15" />

      <path
        d="M66 58h5l3-9 5 20 5-27 5 21 3-5h3"
        className="stroke-current text-primary"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="80" cy="20" r="5" className="fill-current text-primary opacity-80" />
      <circle
        cx="80"
        cy="20"
        r="10"
        className="stroke-current text-primary opacity-25"
        strokeWidth="2"
      />
    </Illustration>
  );
}

// Filtro sem resultado — o oposto do vazio de verdade: a lista existe, o
// recorte é que não achou. A lupa está SOBRE as linhas, não no lugar delas.
export function SearchEmptyIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={40} {...props}>
      <rect x="30" y="20" width="100" height="72" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="30"
        y="20"
        width="100"
        height="72"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {[34, 50, 66].map((y, i) => (
        <rect
          key={y}
          x="44"
          y={y}
          width={i === 1 ? 44 : 56}
          height="6"
          rx="3"
          className="fill-current opacity-[0.15]"
        />
      ))}
      <circle
        cx="98"
        cy="70"
        r="19"
        className="fill-background stroke-current text-primary"
        strokeWidth="2.5"
      />
      <path
        d="m112 84 12 12"
        className="stroke-current text-primary"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="m92 64 12 12m0-12-12 12"
        className="stroke-current text-primary opacity-60"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Áudios de atendimento: o objeto é a gravação que toca no começo da ligação —
// o cartão do arquivo com a onda do som. O play é o acento: o áudio existe
// para ser ouvido, e é a primeira coisa que se faz com ele aqui.
export function GreetingAudioIllustration(props: ComponentProps<"svg">) {
  const barras = [
    { x: 74, h: 16 },
    { x: 82, h: 28 },
    { x: 90, h: 38 },
    { x: 98, h: 22 },
    { x: 106, h: 32 },
    { x: 114, h: 14 },
  ];
  return (
    <Illustration groundY={102} groundRx={42} {...props}>
      <rect x="22" y="26" width="116" height="62" rx="12" className="fill-current opacity-[0.06]" />
      <rect
        x="22"
        y="26"
        width="116"
        height="62"
        rx="12"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="42" y="38" width="42" height="6" rx="3" className="fill-current opacity-30" />
      {barras.map((b) => (
        <rect
          key={b.x}
          x={b.x}
          y={66 - b.h / 2}
          width="4"
          height={b.h}
          rx="2"
          className="fill-current opacity-25"
        />
      ))}
      <circle
        cx="52"
        cy="66"
        r="11"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
      <path d="m49.5 61 7.5 5-7.5 5z" className="fill-current text-primary" />
    </Illustration>
  );
}

// Formulário pós-ligação: o que o vendedor preenche quando desliga. A ficha
// mostra as duas coisas que ele responde ali — um campo escrito e a escolha
// de um desfecho — e o botão de salvar é o acento: o formulário só serve
// quando alguém o responde até o fim.
export function CallFormIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={112} groundRx={38} {...props}>
      <rect x="34" y="10" width="92" height="96" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="34"
        y="10"
        width="92"
        height="96"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="48" y="24" width="38" height="6" rx="3" className="fill-current opacity-30" />

      <rect
        x="48"
        y="36"
        width="64"
        height="14"
        rx="4"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="54" y="41" width="30" height="4" rx="2" className="fill-current opacity-20" />

      {/* A escolha do desfecho: a primeira opção já marcada, porque a ficha
          nunca chega em branco ao vendedor. */}
      <circle cx="52" cy="62" r="4.5" className="fill-current opacity-35" />
      <rect x="62" y="60" width="32" height="4" rx="2" className="fill-current opacity-25" />
      <circle cx="52" cy="76" r="4.5" className="stroke-current opacity-25" strokeWidth="2" />
      <rect x="62" y="74" width="22" height="4" rx="2" className="fill-current opacity-[0.15]" />

      <rect x="48" y="86" width="44" height="14" rx="7" className="fill-current text-primary" />
      <rect x="58" y="91" width="24" height="4" rx="2" className="fill-background" />
    </Illustration>
  );
}

// Horário de atendimento: a semana desenhada como faixas de tempo, uma por dia,
// com o relógio na frente. As faixas curtas no fim são o fim de semana; o
// relógio é o acento porque a decisão ali é declarar as horas em que a marca
// atende.
export function BusinessHoursIllustration(props: ComponentProps<"svg">) {
  const faixas = [
    { y: 30, x: 46, w: 62 },
    { y: 42, x: 46, w: 62 },
    { y: 54, x: 46, w: 58 },
    { y: 66, x: 46, w: 62 },
    { y: 78, x: 46, w: 50 },
  ];
  return (
    <Illustration groundY={108} groundRx={40} {...props}>
      <rect x="20" y="14" width="120" height="82" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="20"
        y="14"
        width="120"
        height="82"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* Coluna dos dias e a faixa de horas de cada um. */}
      {faixas.map((f) => (
        <g key={f.y}>
          <rect x="30" y={f.y} width="10" height="6" rx="3" className="fill-current opacity-25" />
          <rect
            x={f.x}
            y={f.y}
            width={f.w}
            height="6"
            rx="3"
            className="fill-current opacity-[0.18]"
          />
        </g>
      ))}
      <rect x="30" y="90" width="10" height="6" rx="3" className="fill-current opacity-[0.12]" />

      {/* O relógio sai da moldura: é ele que manda na grade toda. */}
      <circle
        cx="112"
        cy="82"
        r="20"
        className="fill-background stroke-current text-primary"
        strokeWidth="2"
      />
      <path
        d="M112 70v12l8 5"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Relatórios sem dado no período: as barras existem, mas fantasmas — o que
// falta é dado, não configuração. O acento é o filtro de período, que é
// justamente o que a pessoa precisa mexer para ver alguma coisa.
export function NoDataIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={44} {...props}>
      <rect x="22" y="20" width="116" height="72" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="22"
        y="20"
        width="116"
        height="72"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect
        x="96"
        y="28"
        width="30"
        height="12"
        rx="6"
        className="stroke-current text-primary"
        strokeWidth="2"
      />
      <rect
        x="102"
        y="32.75"
        width="18"
        height="3.5"
        rx="1.75"
        className="fill-current text-primary opacity-60"
      />
      <path
        d="M40 32v46h80"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="50" y="62" width="10" height="16" rx="3" className="fill-current opacity-[0.12]" />
      <rect x="66" y="52" width="10" height="26" rx="3" className="fill-current opacity-[0.12]" />
      <rect x="82" y="66" width="10" height="12" rx="3" className="fill-current opacity-[0.12]" />
      <rect x="98" y="58" width="10" height="20" rx="3" className="fill-current opacity-[0.12]" />
    </Illustration>
  );
}

// Leads: a lista de quem ainda não existe para o discador. Cada linha é uma
// pessoa com telefone — e o acento é o handset da primeira, porque a única
// coisa que se faz com um lead aqui é ligar para ele.
export function LeadsIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={108} groundRx={42} {...props}>
      <rect x="26" y="16" width="108" height="86" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="26"
        y="16"
        width="108"
        height="86"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <circle cx="46" cy="36" r="7" className="fill-current opacity-25" />
      <rect x="60" y="33" width="40" height="6" rx="3" className="fill-current opacity-20" />
      <circle cx="46" cy="59" r="7" className="fill-current opacity-25" />
      <rect x="60" y="56" width="32" height="6" rx="3" className="fill-current opacity-[0.15]" />
      <circle cx="46" cy="82" r="7" className="fill-current opacity-25" />
      <rect x="60" y="79" width="24" height="6" rx="3" className="fill-current opacity-[0.15]" />
      <circle cx="113" cy="36" r="12" className="stroke-current text-primary" strokeWidth="2" />
      <path
        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
        className="stroke-current text-primary"
        transform="translate(106 29) scale(0.58)"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Usuários: a equipe da marca. Duas pessoas já dentro e a terceira em
// tracejado com o mais — o convite que ainda não foi feito é o acento.
export function TeamIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={92} groundRx={44} {...props}>
      <circle cx="54" cy="44" r="11" className="stroke-current opacity-25" strokeWidth="2" />
      <path
        d="M36 74c0-10 8-18 18-18s18 8 18 18"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="86" cy="44" r="11" className="stroke-current opacity-25" strokeWidth="2" />
      <path
        d="M68 74c0-10 8-18 18-18s18 8 18 18"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle
        cx="120"
        cy="52"
        r="14"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeDasharray="5 4"
      />
      <path
        d="M120 46v12m-6-6h12"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Filas: os leads em ordem de ligação. A seta e o contorno marcam quem é o
// próximo — a fila só significa alguma coisa por causa dessa ordem.
export function QueueIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={40} {...props}>
      <rect x="46" y="22" width="78" height="20" rx="6" className="fill-current opacity-[0.06]" />
      <rect
        x="46"
        y="22"
        width="78"
        height="20"
        rx="6"
        className="stroke-current text-primary"
        strokeWidth="2"
      />
      <rect x="58" y="29" width="34" height="6" rx="3" className="fill-current opacity-25" />
      <rect x="46" y="50" width="78" height="20" rx="6" className="fill-current opacity-[0.06]" />
      <rect
        x="46"
        y="50"
        width="78"
        height="20"
        rx="6"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="58" y="57" width="28" height="6" rx="3" className="fill-current opacity-20" />
      <rect x="46" y="78" width="78" height="20" rx="6" className="fill-current opacity-[0.06]" />
      <rect
        x="46"
        y="78"
        width="78"
        height="20"
        rx="6"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="58" y="85" width="22" height="6" rx="3" className="fill-current opacity-[0.15]" />
      <path
        d="M24 32h12m-5-5 5 5-5 5"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Ramais: o aparelho provisionado. Os arcos são o acento — um ramal só
// serve depois de registrado, e é isso que o sinal diz.
export function ExtensionIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={108} groundRx={32} {...props}>
      <rect x="50" y="22" width="54" height="78" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="50"
        y="22"
        width="54"
        height="78"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="60" y="32" width="34" height="14" rx="4" className="fill-current opacity-[0.15]" />
      <circle cx="64" cy="60" r="3.5" className="fill-current opacity-25" />
      <circle cx="77" cy="60" r="3.5" className="fill-current opacity-25" />
      <circle cx="90" cy="60" r="3.5" className="fill-current opacity-25" />
      <circle cx="64" cy="72" r="3.5" className="fill-current opacity-25" />
      <circle cx="77" cy="72" r="3.5" className="fill-current opacity-25" />
      <circle cx="90" cy="72" r="3.5" className="fill-current opacity-25" />
      <circle cx="64" cy="84" r="3.5" className="fill-current opacity-25" />
      <circle cx="77" cy="84" r="3.5" className="fill-current opacity-25" />
      <circle cx="90" cy="84" r="3.5" className="fill-current opacity-25" />
      <path
        d="M112 40a16 16 0 0 1 0 22"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M120 32a26 26 0 0 1 0 38"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Tabulações: os desfechos que o vendedor escolhe ao encerrar. São etiquetas
// — e a de cima, marcada, é a que vira relatório.
export function DispositionIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={104} groundRx={38} {...props}>
      <rect x="28" y="20" width="104" height="74" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="28"
        y="20"
        width="104"
        height="74"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect
        x="42"
        y="32"
        width="62"
        height="14"
        rx="7"
        className="stroke-current text-primary"
        strokeWidth="2"
      />
      <path
        d="m84 39 4 4 8-8"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="54"
        y="37"
        width="22"
        height="4"
        rx="2"
        className="fill-current text-primary opacity-60"
      />
      <rect x="42" y="54" width="44" height="14" rx="7" className="fill-current opacity-[0.15]" />
      <rect x="42" y="76" width="60" height="14" rx="7" className="fill-current opacity-[0.15]" />
    </Illustration>
  );
}

// Campanhas: o disparo para uma lista inteira. O megafone com as ondas em
// destaque é o alcance — o que a campanha faz e a lista sozinha não faz.
export function CampaignIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={100} groundRx={42} {...props}>
      <rect x="26" y="46" width="14" height="20" rx="5" className="fill-current opacity-25" />
      <path d="M40 44h8l40-18v56L48 64h-8z" className="fill-current opacity-[0.06]" />
      <path
        d="M40 44h8l40-18v56L48 64h-8z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M100 42a18 18 0 0 1 0 26"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M110 32a30 30 0 0 1 0 46"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Illustration>
  );
}

// Objeções: o que o cliente diz e trava a conversa. O balão é a objeção; a
// pílula em destaque é a resposta pronta que a tela ainda não tem.
export function ObjectionIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={108} groundRx={38} {...props}>
      <path
        d="M32 24a8 8 0 0 1 8-8h56a8 8 0 0 1 8 8v26a8 8 0 0 1-8 8H58L44 70V58h-4a8 8 0 0 1-8-8z"
        className="fill-current opacity-[0.06]"
      />
      <path
        d="M32 24a8 8 0 0 1 8-8h56a8 8 0 0 1 8 8v26a8 8 0 0 1-8 8H58L44 70V58h-4a8 8 0 0 1-8-8z"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect x="66" y="26" width="5" height="16" rx="2.5" className="fill-current opacity-30" />
      <circle cx="68.5" cy="48" r="3" className="fill-current opacity-30" />
      <rect
        x="56"
        y="80"
        width="66"
        height="18"
        rx="9"
        className="stroke-current text-primary"
        strokeWidth="2"
      />
      <rect
        x="68"
        y="87"
        width="42"
        height="4"
        rx="2"
        className="fill-current text-primary opacity-60"
      />
    </Illustration>
  );
}

// Script de vendas: a conversa em etapas, na ordem em que o vendedor fala. O
// primeiro passo é o acento — é por ele que se começa a escrever.
export function ScriptIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={114} groundRx={34} {...props}>
      <rect x="38" y="8" width="84" height="98" rx="8" className="fill-current opacity-[0.06]" />
      <rect
        x="38"
        y="8"
        width="84"
        height="98"
        rx="8"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <path
        d="M58 32v48"
        className="stroke-current opacity-[0.15]"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="4 6"
      />
      <circle cx="58" cy="30" r="7" className="stroke-current text-primary" strokeWidth="2" />
      <circle cx="58" cy="30" r="3" className="fill-current text-primary" />
      <rect x="72" y="27" width="34" height="6" rx="3" className="fill-current opacity-25" />
      <circle cx="58" cy="56" r="7" className="stroke-current opacity-25" strokeWidth="2" />
      <rect x="72" y="53" width="28" height="6" rx="3" className="fill-current opacity-20" />
      <circle cx="58" cy="82" r="7" className="stroke-current opacity-25" strokeWidth="2" />
      <rect x="72" y="79" width="22" height="6" rx="3" className="fill-current opacity-[0.15]" />
    </Illustration>
  );
}

// Entregas de automação: o disparo que sai daqui e chega do outro lado. O
// alvo com o check é o acento — a entrega só conta quando é aceita.
export function DeliveryIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={94} groundRx={44} {...props}>
      <rect x="18" y="34" width="56" height="44" rx="8" className="fill-current opacity-[0.06]" />
      <rect
        x="18"
        y="34"
        width="56"
        height="44"
        rx="8"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="30" y="46" width="32" height="6" rx="3" className="fill-current opacity-25" />
      <rect x="30" y="58" width="20" height="6" rx="3" className="fill-current opacity-[0.15]" />
      <path
        d="M82 56h20m-6-5 6 5-6 5"
        className="stroke-current opacity-25"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="124" cy="56" r="16" className="stroke-current text-primary" strokeWidth="2" />
      <path
        d="m116 56 6 6 10-12"
        className="stroke-current text-primary"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Illustration>
  );
}

// Instalar o app: a janela do computador atrás e o celular na frente, cada um
// com a grade de ícones da área de trabalho. O acento é o ícone do Disk já
// instalado no celular — o resultado da ação, não o botão que a dispara.
export function InstallAppIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={110} groundRx={46} {...props}>
      {/* Janela do computador. */}
      <rect x="16" y="16" width="106" height="76" rx="9" className="fill-current opacity-[0.06]" />
      <rect
        x="16"
        y="16"
        width="106"
        height="76"
        rx="9"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* Barra de título com os três controles da janela. */}
      <path d="M16 32h106" className="stroke-current opacity-25" strokeWidth="2" />
      {[26, 34, 42].map((cx) => (
        <circle key={cx} cx={cx} cy="24" r="2.5" className="fill-current opacity-25" />
      ))}
      {/* Grade de ícones da área de trabalho. */}
      {[44, 66].map((y) =>
        [28, 50, 72].map((x) => (
          <rect
            key={`${x}-${y}`}
            x={x}
            y={y}
            width="14"
            height="14"
            rx="4"
            className="fill-current opacity-[0.16]"
          />
        )),
      )}

      {/* Celular na frente, recortando a janela: a mesma instalação nos dois. */}
      {/* Dois retângulos, não um: `opacity` no elemento inteiro deixaria o
          preenchimento translúcido e a grade da janela apareceria através do
          celular. O fundo fica sólido; só o contorno é atenuado. */}
      <rect x="98" y="38" width="46" height="66" rx="10" className="fill-background" />
      <rect
        x="98"
        y="38"
        width="46"
        height="66"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="114" y="44" width="14" height="3" rx="1.5" className="fill-current opacity-25" />
      {/* O ícone do Disk instalado — o único acento. */}
      <rect x="106" y="54" width="14" height="14" rx="4" className="fill-current text-primary" />
      <rect x="124" y="54" width="14" height="14" rx="4" className="fill-current opacity-[0.16]" />
      <rect x="106" y="74" width="14" height="14" rx="4" className="fill-current opacity-[0.16]" />
      <rect x="124" y="74" width="14" height="14" rx="4" className="fill-current opacity-[0.16]" />
      <rect x="112" y="94" width="20" height="3" rx="1.5" className="fill-current opacity-25" />
    </Illustration>
  );
}

// Instalado, sem o peso do navegador: a barra de abas e a barra de endereço
// saem de cima da janela (o bloco solto lá em cima, atenuado) e o conteúdo
// ocupa o espaço que era delas — é o conteúdo que fica em primary.
export function NoBrowserChromeIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={108} groundRx={44} {...props}>
      {/* O que sai: barra de abas e barra de endereço, soltas e desalinhadas. */}
      <g transform="rotate(-4 80 22)">
        <rect x="26" y="8" width="32" height="9" rx="3" className="fill-current opacity-30" />
        <rect x="62" y="8" width="32" height="9" rx="3" className="fill-current opacity-20" />
        <rect
          x="26"
          y="22"
          width="98"
          height="9"
          rx="4.5"
          className="fill-current opacity-[0.18]"
        />
      </g>

      {/* A janela do app, já sem elas. */}
      <rect x="22" y="40" width="116" height="58" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="22"
        y="40"
        width="116"
        height="58"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* Conteúdo colado no topo: o espaço das barras virou tela útil. */}
      <rect x="34" y="52" width="52" height="7" rx="3.5" className="fill-current text-primary" />
      <rect x="34" y="66" width="92" height="6" rx="3" className="fill-current opacity-[0.18]" />
      <rect x="34" y="78" width="72" height="6" rx="3" className="fill-current opacity-[0.18]" />
    </Illustration>
  );
}

// Uma janela só sua: o Disk deixa de ser mais uma aba e vira janela com lugar
// na barra de tarefas — o acento é o ícone dele lá embaixo, entre os outros.
export function OwnWindowIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={112} groundRx={44} {...props}>
      {/* Janelas atrás: as outras coisas abertas na máquina. */}
      <rect
        x="34"
        y="12"
        width="98"
        height="52"
        rx="9"
        className="fill-background stroke-current opacity-[0.18]"
        strokeWidth="2"
      />
      {/* A janela do Disk, na frente e inteira. */}
      <rect x="20" y="24" width="98" height="54" rx="9" className="fill-background" />
      <rect x="20" y="24" width="98" height="54" rx="9" className="fill-current opacity-[0.06]" />
      <rect
        x="20"
        y="24"
        width="98"
        height="54"
        rx="9"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      <rect x="32" y="36" width="40" height="6" rx="3" className="fill-current opacity-30" />
      <rect x="32" y="50" width="66" height="5" rx="2.5" className="fill-current opacity-[0.18]" />
      <rect x="32" y="61" width="50" height="5" rx="2.5" className="fill-current opacity-[0.18]" />

      {/* Barra de tarefas: o lugar reservado que uma aba nunca teve. */}
      <rect x="24" y="86" width="112" height="20" rx="9" className="fill-current opacity-[0.09]" />
      <rect x="34" y="91" width="10" height="10" rx="3" className="fill-current text-primary" />
      <rect x="50" y="91" width="10" height="10" rx="3" className="fill-current opacity-20" />
      <rect x="66" y="91" width="10" height="10" rx="3" className="fill-current opacity-20" />
    </Illustration>
  );
}

// Abre mais rápido: os arquivos do app já estão no aparelho, então a barra de
// carregamento termina antes — o trecho preenchido é o acento.
export function FastStartIllustration(props: ComponentProps<"svg">) {
  return (
    <Illustration groundY={106} groundRx={40} {...props}>
      <rect x="30" y="16" width="100" height="70" rx="10" className="fill-current opacity-[0.06]" />
      <rect
        x="30"
        y="16"
        width="100"
        height="70"
        rx="10"
        className="stroke-current opacity-25"
        strokeWidth="2"
      />
      {/* O ícone do app já pronto na tela. */}
      <rect x="66" y="28" width="28" height="28" rx="9" className="fill-current text-primary" />
      <path d="M82 33l-7 10h6l-3 8 8-10h-6l2-8z" className="fill-background" />
      {/* Barra de carregamento quase no fim. */}
      <rect x="44" y="66" width="72" height="8" rx="4" className="fill-current opacity-[0.18]" />
      <rect x="44" y="66" width="56" height="8" rx="4" className="fill-current text-primary" />
    </Illustration>
  );
}
